import Loader from "../common/Loader";
import ConfigureTab from "./ConfigureTab";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { TeamsFxContext } from "./TeamsFxContext";
import RemoveTab from "./RemoveTab";
import ContactSales from "./ContactSales";
import InviteBotHelpDialog from "./InviteBotHelpDialog";
import SidePanel from "./SidePanel";
import AdhocSidePanel from "./AdhocSidePanel";
import { useTeamsLogin } from "../../hooks/user";
import { HashRouter, Route, Routes } from "react-router-dom";
import { OrganizationProvider } from "../context/organization/OrganizationProvider";
import { RoomProvider } from "../context/room/RoomProvider";
import { Settings } from "../common/dialogs/SettingsDialog";
import Header from "../Header";
import { useTeamsMeetingType } from "../../hooks/teams";
import { useRoom } from "../context/room/room";
import { isDevAuthBypassEnabled } from "../../hooks/devAuthBypass";

function MeetingTypeRouter() {
  const meetingType = useTeamsMeetingType();
  const { organization } = useRoom();
  if (organization?.teams_adhoc_enabled && meetingType === "adhoc") {
    return <AdhocSidePanel />;
  }
  return <SidePanel />;
}

function TeamsAuthenticator(props: { children: React.ReactNode }) {
  const { loading } = useTeamsLogin();

  if (loading) {
    return <Loader />;
  }

  return <>{props.children}</>;
}

export default function TeamsRouter() {
  const isStub = !!import.meta.env.VITE_STUB;
  // Real Teams SSO can never succeed against an ad-hoc tunnel domain (the
  // AAD app registration is pinned to a fixed production redirect URI) -
  // VITE_DEV_AUTH_BYPASS is the single switch for every dev-auth-bypass
  // behavior on the frontend (see vite-env.d.ts and hooks/axios.ts), so
  // skipping real SSO piggybacks on that same var instead of having its
  // own. Stub mode always skips it too, for its own separate reason (all
  // data is fake, so there's nothing real to authenticate against).
  const devAuthBypass = isStub || isDevAuthBypassEnabled();

  // Init teams app. Called unconditionally (Rules of Hooks) even when
  // skipping SSO, but its result is ignored below - we never want to block
  // on a real Azure AD sign-in that isn't configured for the current
  // origin. useTeamsLogin() falls back to teamsUserCredential only if the
  // /v1/auth/login call fails, so undefined here is safe.
  // `||`, not `??`: an unset var in example.env/.env parses to "", not
  // undefined, so `??` never falls through to the default and
  // TeamsUserCredential throws ("clientId in configuration is invalid")
  // before dev-bypass ever gets a chance to ignore this hook's result.
  const { loading, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint:
      import.meta.env.VITE_TEAMS_START_LOGIN_PAGE_URL ||
      "https://localhost:5173/auth-start.html",
    clientId:
      import.meta.env.VITE_TEAMS_CLIENT_ID ||
      "9b234b7b-9c76-4a2d-aef7-710a99c91c62",
  });

  if (!devAuthBypass && loading) {
    return <Loader />;
  }

  return (
    <TeamsFxContext.Provider
      value={{
        teamsUserCredential: devAuthBypass ? undefined : teamsUserCredential,
      }}
    >
      <TeamsAuthenticator>
        <HashRouter>
          <Routes>
            {/* TODO: Update manifest (requires re-submit to teams store) */}
            <Route
              path="/tab"
              element={
                <>
                  <Header hideUserNav={true} disableLogoClick={true} />
                  <ContactSales />
                </>
              }
            />
            <Route
              path="/configure"
              element={
                <>
                  <OrganizationProvider>
                    <ConfigureTab />
                  </OrganizationProvider>
                </>
              }
            />
            <Route
              path="/teams/room/:id"
              element={
                <>
                  <RoomProvider>
                    <MeetingTypeRouter />
                  </RoomProvider>
                </>
              }
            />
            <Route
              path="/teams/settings/:id"
              element={
                <>
                  <RoomProvider>
                    <Settings
                      hiddenCategories={["audio", "video", "text-translation"]}
                      teams={true}
                    />
                  </RoomProvider>
                </>
              }
            />
            <Route
              path="/teams/invite-bot-help"
              element={<InviteBotHelpDialog />}
            />
            <Route path="/teams/room/:id/remove" element={<RemoveTab />} />
          </Routes>
        </HashRouter>
      </TeamsAuthenticator>
    </TeamsFxContext.Provider>
  );
}
